import {
  DragDropProvider,
  DragOverlay,
  useDragDropMonitor,
} from "@dnd-kit/react";
import { Info } from "lucide-react";
import { useState } from "react";
import type { PropsWithChildren } from "react";
import { createPortal } from "react-dom";
import { toast } from "sonner";

import {
  useBatchAddItemTagsMutation,
  useMoveItemsMutation,
} from "../hooks";
import {
  isFolderMoveDropData,
  isItemMoveDragData,
  isTagAddDropData,
  type ItemMoveDragData,
} from "../item-move-dnd";
import { getItemTypeConfig } from "./items-display/display-helpers";

interface DragPreviewState {
  data: ItemMoveDragData;
  x: number;
  y: number;
}

const MAX_VISIBLE_TITLES = 8;

function ItemMoveDragPreview() {
  const [preview, setPreview] = useState<DragPreviewState | null>(null);

  const updatePreview = (operation: {
    source: { data: unknown } | null;
    position: { current: { x: number; y: number } };
  }) => {
    if (!isItemMoveDragData(operation.source?.data)) {
      return;
    }

    const visibleCount = Math.min(
      operation.source.data.items.length,
      MAX_VISIBLE_TITLES,
    );
    const previewHeight =
      visibleCount * 28 +
      (operation.source.data.items.length > MAX_VISIBLE_TITLES ? 64 : 44);

    setPreview({
      data: operation.source.data,
      x: Math.max(
        8,
        Math.min(operation.position.current.x + 12, window.innerWidth - 296),
      ),
      y: Math.max(
        8,
        Math.min(
          operation.position.current.y + 10,
          window.innerHeight - previewHeight - 8,
        ),
      ),
    });
  };

  useDragDropMonitor({
    onDragStart: ({ operation }) => updatePreview(operation),
    onDragMove: ({ operation }) => updatePreview(operation),
    onDragEnd: () => setPreview(null),
  });

  return (
    <>
      <DragOverlay dropAnimation={null}>
        <span className="block size-px opacity-0" />
      </DragOverlay>
      {preview && createPortal(
        <div
          className="shadow-floating pointer-events-none fixed z-[100] w-72 rounded-lg bg-background/95 px-3 py-2 text-sm backdrop-blur"
          style={{ left: preview.x, top: preview.y }}
        >
          <div className="mb-1 flex items-center gap-1.5 text-sm text-muted-foreground/65">
            <Info className="size-3.5 shrink-0" />
            <span className="truncate">拖到文件夹可移动，拖到标签可添加</span>
          </div>
          {preview.data.items.slice(0, MAX_VISIBLE_TITLES).map((item) => {
            const typeConfig = getItemTypeConfig(item.format);
            const TypeIcon = typeConfig.icon;

            return (
              <div
                key={item.id}
                className="flex h-7 min-w-0 items-center gap-2"
              >
                <TypeIcon
                  size={14}
                  className={`shrink-0 ${typeConfig.color}`}
                />
                <span className="truncate font-medium">{item.title}</span>
              </div>
            );
          })}
          {preview.data.items.length > MAX_VISIBLE_TITLES && (
            <div className="pt-1 text-sm text-foreground">
              … +{preview.data.items.length - MAX_VISIBLE_TITLES}
            </div>
          )}
        </div>,
        document.body,
      )}
    </>
  );
}

export function ItemMoveDndProvider({ children }: PropsWithChildren) {
  const moveItemsMutation = useMoveItemsMutation();
  const addTagsMutation = useBatchAddItemTagsMutation();

  return (
    <DragDropProvider
      onDragEnd={(event) => {
        if (event.canceled) {
          return;
        }

        const sourceData = event.operation.source?.data;
        const targetData = event.operation.target?.data;

        if (!isItemMoveDragData(sourceData)) {
          return;
        }

        if (isFolderMoveDropData(targetData)) {
          moveItemsMutation.mutate(
            {
              items: sourceData.items,
              folderId: targetData.folderId === "0" ? null : targetData.folderId,
            },
            {
              onSuccess: ({ movedCount }) => {
                if (movedCount === 0) {
                  return;
                }

                toast.success(
                  movedCount > 1
                    ? `已将 ${movedCount} 个收藏移动到「${targetData.folderName}」`
                    : `已移动到「${targetData.folderName}」`,
                );
                sourceData.onMoved?.();
              },
            },
          );
          return;
        }

        if (isTagAddDropData(targetData)) {
          const itemIds = sourceData.items
            .filter((item) => !item.tagIds.includes(targetData.tagId))
            .map((item) => item.id);

          if (itemIds.length === 0) {
            return;
          }

          addTagsMutation.mutate(
            {
              ids: itemIds,
              tag_ids: [targetData.tagId],
            },
            {
              onSuccess: () => {
                toast.success(
                  itemIds.length > 1
                    ? `已为 ${itemIds.length} 个收藏添加标签「${targetData.tagName}」`
                    : `已添加标签「${targetData.tagName}」`,
                );
              },
            },
          );
        }
      }}
    >
      {children}
      <ItemMoveDragPreview />
    </DragDropProvider>
  );
}
